import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useLottie } from "lottie-react";
import { useAddResponse, useGetMatch } from "../hooks";
import padelBall from "./padel-ball.json";
import "./match-response.css";
import Logo from "./logo.png";

const padelBallOptions = {
  animationData: padelBall,
  loop: true,
};
const padelBallStyle = {
  width: "200px",
  margin: "-20px",
};

const formatTime = (time: string): string => {
  // Remove seconds if present (HH:mm:ss -> HH:mm)
  return time.split(":").slice(0, 2).join(":");
};

export const MatchResponse = () => {
  const { t } = useTranslation();
  const { matchId } = useParams<{ matchId: string }>();
  const { data, isLoading, error } = useGetMatch(matchId);
  const { mutateAsync: addResponse, isPending: isSubmitting } =
    useAddResponse();
  const { View: PadelBall } = useLottie(padelBallOptions, padelBallStyle);

  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submittedStatus, setSubmittedStatus] = useState<
    "confirmed" | "rejected" | null
  >(null);
  const [nameError, setNameError] = useState("");

  const handleResponse = async (status: "confirmed" | "rejected") => {
    if (!name.trim()) {
      setNameError(t("invite.nameRequired"));
      return;
    }

    setNameError("");

    if (!matchId) {
      alert(t("invite.missingId"));
      return;
    }

    try {
      await addResponse({
        matchId,
        name: name.trim(),
        status,
      });
      setSubmitted(true);
      setSubmittedStatus(status);
    } catch {
      alert(t("invite.submitError"));
    }
  };

  if (isLoading || isSubmitting) {
    return (
      <div className="match-response-container">
        <header className="match-header">
          <div className="match-title-container">
            <img src={Logo} alt="PinPadel" className="logo-header" />
            <h3 className="match-title-logo">pinpadel</h3>
          </div>
          <div className="match-header-actions">
            <a
              href="https://apps.apple.com/es/app/pinpadel/id6756853010"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-download-app"
            >
              {t("invite.download")}
            </a>
          </div>
        </header>
        <div className="match-content">
          <div className="loading">{PadelBall}</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="match-response-container">
        <header className="match-header">
          <div className="match-title-container">
            <img src={Logo} alt="PinPadel" className="logo-header" />
            <h3 className="match-title-logo">pinpadel</h3>
          </div>
          <div className="match-header-actions">
            <a
              href="https://apps.apple.com/es/app/pinpadel/id6756853010"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-download-app"
            >
              {t("invite.download")}
            </a>
          </div>
        </header>
        <div className="match-content">
          <h1 className="error-title">{t("invite.oops")}</h1>
          <p className="error-subtitle">{t("invite.genericError")}</p>
        </div>
      </div>
    );
  }

  if (data.status === "closed" || data.spotsNeeded === 0) {
    return (
      <div className="match-response-container">
        <header className="match-header">
          <div className="match-title-container">
            <img src={Logo} alt="PinPadel" className="logo-header" />
            <h3 className="match-title-logo">pinpadel</h3>
          </div>
          <div className="match-header-actions">
            <a
              href="https://apps.apple.com/es/app/pinpadel/id6756853010"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-download-app"
            >
              {t("invite.download")}
            </a>
          </div>
        </header>
        <div className="match-content">
          <h1 className="match-title">
            {data.status === "closed" ? t("invite.closed") : t("invite.full")}
          </h1>
          <p className="match-subtitle">
            {data.status === "closed"
              ? t("invite.closedHint")
              : t("invite.fullHint")}
          </p>
          <div className="match-info">
            <h3 className="match-location">{data.location}</h3>
            <div className="match-details">
              <div className="match-detail-item">
                <span className="match-detail-emoji">📅</span>
                <span className="match-detail-text">{data.date}</span>
              </div>
              <div className="match-detail-item">
                <span className="match-detail-emoji">⏰</span>
                <span className="match-detail-text">
                  {formatTime(data.time)}
                </span>
              </div>
              {data.note && (
                <div className="match-detail-item">
                  <span className="match-detail-emoji">📝</span>
                  <span className="match-detail-text">{data.note}</span>
                </div>
              )}
            </div>
            <div className="match-spots">
              <span className="match-spots-label">{t("invite.spots")}</span>
              <span className="match-spots-number">0</span>
            </div>
          </div>
          <h3 className="match-create-title">{t("invite.createInApp")}</h3>
        </div>
      </div>
    );
  }

  if (submitted && submittedStatus) {
    return (
      <div className="match-response-container">
        <header className="match-header">
          <div className="match-title-container">
            <img src={Logo} alt="PinPadel" className="logo-header" />
            <h3 className="match-title-logo">pinpadel</h3>
          </div>
          <div className="match-header-actions">
            <a
              href="https://apps.apple.com/es/app/pinpadel/id6756853010"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-download-app"
            >
              {t("invite.download")}
            </a>
          </div>
        </header>
        <div className="match-content">
          <div className="success-container">
            <div className="success-card">
              <div className="success-icon">
                {submittedStatus === "confirmed" ? "🎾" : "👍"}
              </div>
              <h1 className="success-title">
                {submittedStatus === "confirmed"
                  ? t("invite.thanks")
                  : t("invite.gotIt")}
              </h1>
              <p className="success-subtitle">
                {submittedStatus === "confirmed"
                  ? t("invite.confirmedBody", { name })
                  : t("invite.rejectedBody")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="match-response-container">
      <header className="match-header">
        <div className="match-title-container">
          <img src={Logo} alt="PinPadel" className="logo-header" />
          <h3 className="match-title-logo">pinpadel</h3>
        </div>
        <div className="match-header-actions">
          <a
            href="https://apps.apple.com/es/app/pinpadel/id6756853010"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-download-app"
          >
            {t("invite.download")}
          </a>
        </div>
      </header>
      <div className="match-content">
        <h1 className="match-title">{t("invite.title")}</h1>
        <p className="match-subtitle">{t("invite.subtitle")}</p>

        <div className="match-info">
          <h3 className="match-location">{data.location}</h3>
          <div className="match-details">
            <div className="match-detail-item">
              <span className="match-detail-emoji">📅</span>
              <span className="match-detail-text">{data.date}</span>
            </div>
            <div className="match-detail-item">
              <span className="match-detail-emoji">⏰</span>
              <span className="match-detail-text">{formatTime(data.time)}</span>
            </div>
            {data.note && (
              <div className="match-detail-item">
                <span className="match-detail-emoji">📝</span>
                <span className="match-detail-text">{data.note}</span>
              </div>
            )}
          </div>
          <div className="match-spots">
            <span className="match-spots-label">{t("invite.spots")}</span>
            <span className="match-spots-number">{data.spotsNeeded}</span>
          </div>
        </div>

        <h2 className="match-question-title">{t("invite.question")}</h2>

        <div className="name-input-container">
          <input
            type="text"
            className={`name-input ${nameError ? "name-input-error" : ""}`}
            placeholder={t("invite.namePlaceholder")}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError("");
            }}
            maxLength={50}
            disabled={isSubmitting}
          />
          {nameError && <p className="name-error-message">{nameError}</p>}
        </div>

        <div className="buttons-container">
          <button
            className="btn btn-confirm"
            onClick={() => handleResponse("confirmed")}
            disabled={isSubmitting}
          >
            {t("invite.yes")}
          </button>
          <button
            className="btn btn-reject"
            onClick={() => handleResponse("rejected")}
            disabled={isSubmitting}
          >
            {t("invite.no")}
          </button>
        </div>
      </div>
    </div>
  );
};
